import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, DownloadIcon, FileTextIcon, HeadphonesIcon, SparklesIcon, XIcon } from 'lucide-react';
import { atnaQuestions, consents, invoices, policyDocs } from '../../data/profil';
import type { PanelId } from './AccountNav';
const titles: Record<PanelId, string> = {
  consentements: 'Consentements',
  factures: 'Mes factures',
  confidentialite: 'Confidentialité et transparence',
  atna: 'Aide ATNA'
};
interface ConsentEvent {
  id: string;
  date: string;
  label: string;
  title: string;
  withdrawn?: boolean;
}

/** Historique initial : la date d'octroi de chaque consentement. */
const initialHistory: ConsentEvent[] = consents.map(consent => ({
  id: `init-${consent.id}`,
  date: consent.grantedOn.replace(/^(Accordé|Demandé) le /, ''),
  label: consent.status === 'accorde' ? 'Consentement accordé' : 'Consentement demandé',
  title: consent.title
})).reverse();
function nowLabel() {
  const now = new Date();
  return `Aujourd’hui, ${now.getHours()} h ${String(now.getMinutes()).padStart(2, '0')}`;
}
function ConsentsPanel() {
  const [withdrawn, setWithdrawn] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<string | null>(null);
  const [history, setHistory] = useState<ConsentEvent[]>(initialHistory);
  const withdraw = (id: string, title: string) => {
    const date = nowLabel();
    setWithdrawn(current => ({
      ...current,
      [id]: date
    }));
    setHistory(current => [{
      id: `retrait-${id}-${Date.now()}`,
      date,
      label: 'Consentement retiré',
      title,
      withdrawn: true
    }, ...current]);
    setConfirming(null);
  };
  return <div data-id="element-2703" data-mp="1oimjsp0xsfoe">
    <ul className="space-y-3" data-id="element-2704" data-mp="1okfse9v419z1">
      {consents.map(consent => {
        const withdrawnOn = withdrawn[consent.id];
        return <li key={consent.id} className={`rounded-2xl border p-5 transition-colors duration-200 ease-ville ${withdrawnOn ? 'border-slate-200 bg-slate-50' : 'border-slate-200'}`} data-id="element-2705" data-mp="1akenmjbq5iea">
          <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-2706" data-mp="3chf7tfeaq70d">
            <h3 className={`text-[15px] font-bold ${withdrawnOn ? 'text-slate-500 line-through decoration-slate-400' : 'text-ville-navy'}`} data-id="element-2707" data-mp="3cfc6hz7p0rgc">
              {consent.title}
            </h3>
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${withdrawnOn ? 'bg-slate-200 text-slate-600' : consent.status === 'accorde' ? 'bg-ville-indigo-soft text-ville-indigo' : 'bg-amber-50 text-amber-800'}`} data-id="element-2708" data-mp="41n1andlbh9l">
              {withdrawnOn ? 'Retiré' : consent.status === 'accorde' ? 'Autorisé' : 'À confirmer'}
            </span>
          </div>
          <p className={`mt-2 text-sm leading-relaxed ${withdrawnOn ? 'text-slate-400 line-through decoration-slate-300' : 'text-slate-600'}`} data-id="element-2709" data-mp="2arjtbg5w16zl">
            {consent.purpose}
          </p>
          <p className="mt-2 text-xs text-slate-500" data-id="element-2710" data-mp="25n8yhk46ae4w">
            {withdrawnOn ? `Retiré le ${withdrawnOn.replace('Aujourd’hui', 'aujourd’hui')}` : consent.grantedOn}
          </p>
          {withdrawnOn && consent.withdrawImpact && <p className="mt-1 text-xs font-semibold text-slate-600" data-id="element-2711" data-mp="2jemh8vu9b7yv">{consent.withdrawImpact}</p>}
          {!withdrawnOn && <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-2712" data-mp="2oc5v3un1liqa">
            {confirming === consent.id ? <div role="group" aria-label="Confirmer le retrait" className="flex w-full flex-wrap items-center gap-3 rounded-xl bg-ville-sand px-4 py-3" data-id="element-2713" data-mp="2tdbntsgpd5zt">
                <div className="mr-auto" data-id="element-2714" data-mp="yzarbe8mkv2w">
                  <p className="text-sm font-semibold text-ville-navy" data-id="element-2715" data-mp="z0dfheekcjf4">Retirer ce consentement ?</p>
                  {consent.withdrawImpact && <p className="mt-0.5 text-sm text-slate-600" data-id="element-2716" data-mp="2uepmvo75kq2a">{consent.withdrawImpact}</p>}
                </div>
                <button type="button" onClick={() => setConfirming(null)} className="text-sm font-semibold text-slate-600 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2717" data-mp="2pfwh6hy3y3co">
                  Annuler
                </button>
                <button type="button" onClick={() => withdraw(consent.id, consent.title)} className="rounded-full bg-ville-navy px-4 py-2 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-2718" data-mp="2arji3c3q44dy">
                  Confirmer le retrait
                </button>
              </div> : <>
            {consent.withdrawable ? <button type="button" onClick={() => setConfirming(consent.id)} className="text-sm font-semibold text-slate-500 underline underline-offset-4 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2719" data-mp="2ilgw5elm8bz">
                Retirer mon consentement
              </button> : <button type="button" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-4 py-2 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-2720" data-mp="m7dtq3x6fgt7">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-2721" data-mp="ha44opdp975w" />
                Confirmer
              </button>}
            </>}
          </div>}
        </li>;
      })}
    </ul>

    <section aria-labelledby="historique-consentements" className="mt-8" data-id="element-2722" data-mp="2hw4a3h9yzz8y">
      <h3 id="historique-consentements" className="text-[15px] font-extrabold text-ville-navy" data-id="element-2723" data-mp="2hy8d7x01ou86">
        Historique
      </h3>
      <ol aria-live="polite" className="mt-3 border-l border-slate-200 pl-5" data-id="element-2724" data-mp="293k9wce15mm0">
        <AnimatePresence initial={false} data-id="element-2725" data-mp="291h9qzj5yd3k">
          {history.map(event => <motion.li key={event.id} layout initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.22,
            ease: [0.23, 1, 0.32, 1]
          }} className="relative pb-4 last:pb-0" data-id="element-2726" data-mp="244h5mu5vjkf3">
              <span aria-hidden="true" className={`absolute -left-[25px] top-1.5 h-2 w-2 rounded-full ${event.withdrawn ? 'bg-slate-500' : 'bg-ville-indigo'}`} data-id="element-2727" data-mp="1q57zknbdu6ou" />
              <p className="text-sm font-bold text-ville-navy" data-id="element-2728" data-mp="3fjctn6qeuy8b">{event.label}</p>
              <p className="text-sm text-slate-600" data-id="element-2729" data-mp="3n3f1zermwi4y">{event.title}</p>
              <p className="text-xs text-slate-500" data-id="element-2730" data-mp="3n35afmqxy2x5">{event.date}</p>
            </motion.li>)}
        </AnimatePresence>
      </ol>
    </section>
    </div>;
}
function InvoicesPanel() {
  return <ul className="space-y-3" data-id="element-2731" data-mp="pxbin4rxjxm">
      {invoices.map(invoice => <li key={invoice.id} className="rounded-2xl border border-slate-200 p-5" data-id="element-2732" data-mp="5lm240ytfqhs">
          <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-2733" data-mp="345cjzjxi7lgn">
            <div className="min-w-0" data-id="element-2734" data-mp="393e0bx3jp6of">
              <p className="font-bold text-ville-navy" data-id="element-2735" data-mp="394fc63sdxx77">{invoice.service}</p>
              <p className="text-sm text-slate-500" data-id="element-2736" data-mp="3e2h7bn335mz1">
                {invoice.date} · {invoice.reference}
              </p>
            </div>
            <div className="text-right" data-id="element-2737" data-mp="3i5n9ojn2uu08">
              <p className="text-lg font-extrabold text-ville-navy" data-id="element-2738" data-mp="tcrrp7uk5x5a">{invoice.amount}</p>
              <span className={`mt-1 inline-block rounded-full px-3 py-1 text-xs font-bold ${invoice.status === 'payee' ? 'bg-slate-100 text-slate-600' : 'bg-ville-indigo-soft text-ville-indigo'}`} data-id="element-2739" data-mp="xe38qozd9a1v">
                {invoice.status === 'payee' ? 'Payée' : 'À payer'}
              </span>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-2740" data-mp="1t79lfaxrishn">
            <button type="button" className="text-sm font-bold text-ville-indigo underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2741" data-mp="1odf4gf9jkrm9">
              Voir la facture
            </button>
            <button type="button" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2742" data-mp="2c2zpr4t640yp">
              <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-2743" data-mp="cr6pmzb32fhr" />
              Télécharger
            </button>
          </div>
        </li>)}
    </ul>;
}
function PrivacyPanel() {
  return <ul className="space-y-3" data-id="element-2744" data-mp="2tdn1ddl0fdkl">
      {policyDocs.map(doc => <li key={doc.id} className="rounded-2xl border border-slate-200 p-5" data-id="element-2745" data-mp="2tctri3qr87y8">
          <div className="flex items-start gap-4" data-id="element-2746" data-mp="2enxpanbdpp5j">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-ville-navy" aria-hidden="true" data-id="element-2747" data-mp="2jntpsckvaygc">
              <FileTextIcon className="h-5 w-5" data-id="element-2748" data-mp="25mzglrpzwtgm" />
            </span>
            <div className="min-w-0" data-id="element-2749" data-mp="29pe26jl1u7bl">
              <h3 className="text-[15px] font-bold text-ville-navy" data-id="element-2750" data-mp="29qfe0q9w2xud">{doc.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600" data-id="element-2751" data-mp="1vqo0srr75lhf">{doc.description}</p>
              <a href="#profil" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-ville-indigo hover:underline" data-id="element-2752" data-mp="20o64a2mg38i1">
                Consulter
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2753" data-mp="3v1xkjkza4qmg" />
              </a>
            </div>
          </div>
        </li>)}
    </ul>;
}
function AtnaPanel() {
  return <div className="space-y-6" data-id="element-2754" data-mp="1vxl3adlm5fnt">
      <div className="flex items-start gap-3 rounded-2xl bg-ville-indigo-soft p-5" data-id="element-2755" data-mp="1vywakne9p5jo">
        <SparklesIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-2756" data-mp="1i77uya4t7yxy" />
        <p className="text-sm leading-relaxed text-ville-navy" data-id="element-2757" data-mp="1n5sxwhbg4wq3">
          ATNA répond à vos questions sur votre compte, vos données et vos services. Ses réponses sont supervisées par
          la Ville et ne remplacent jamais un contact humain.
        </p>
      </div>

      <div data-id="element-2758" data-mp="20wxpqx8kokpv">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500" data-id="element-2759" data-mp="20x7gin1fyvwm">Questions fréquentes</h3>
        <ul className="mt-3 space-y-2" data-id="element-2760" data-mp="25ug2yztmhm3o">
          {atnaQuestions.map(question => <li key={question} data-id="element-2761" data-mp="pgzm58w5yqw1">
              <button type="button" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2762" data-mp="pfoayo0e2guw">
                {question}
              </button>
            </li>)}
        </ul>
      </div>

      <div className="rounded-2xl border border-slate-200 p-5" data-id="element-2763" data-mp="qy6eajkubp97">
        <div className="flex items-start gap-3" data-id="element-2764" data-mp="qwlh0m755s7n">
          <HeadphonesIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-navy" aria-hidden="true" data-id="element-2765" data-mp="39vgxqecs7po" />
          <p className="text-sm leading-relaxed text-slate-600" data-id="element-2766" data-mp="3vdo4b504o66v">
            Vous préférez parler à une personne ? Un agent vie privée peut vous répondre du lundi au vendredi.
          </p>
        </div>
        <button type="button" className="mt-4 inline-flex items-center justify-center rounded-full border border-ville-navy px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-navy hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-2767" data-mp="8756d61qsjdi">
          Contacter un agent
        </button>
      </div>
    </div>;
}
const panels: Record<PanelId, () => JSX.Element> = {
  consentements: ConsentsPanel,
  factures: InvoicesPanel,
  confidentialite: PrivacyPanel,
  atna: AtnaPanel
};
interface AccountPanelProps {
  panel: PanelId | null;
  onClose: () => void;
}
export function AccountPanel({
  panel,
  onClose
}: AccountPanelProps) {
  const Content = panel ? panels[panel] : null;
  return <AnimatePresence data-id="element-2768" data-mp="11nu2ar4924zd">
      {panel && Content && <motion.div className="fixed inset-0 z-50 flex justify-end" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-2769" data-mp="16kkq60maq4vc">
          <button type="button" aria-label="Fermer le panneau" onClick={onClose} className="absolute inset-0 bg-ville-navy/40" data-id="element-2770" data-mp="2i2c1l7l20oyr" />
          <motion.aside role="dialog" aria-modal="true" aria-label={titles[panel]} initial={{
        x: 40,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 40,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative flex h-full w-full max-w-xl flex-col overflow-y-auto bg-white shadow-2xl" data-id="element-2771" data-mp="agbz6jejxs8p">
            <header className="sticky top-0 flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-6 py-5" data-id="element-2772" data-mp="3itjb97hah4br">
              <h2 className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2773" data-mp="3et8v51zs8xj6">{titles[panel]}</h2>
              <button type="button" onClick={onClose} aria-label="Fermer" className="grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2774" data-mp="39uo15kb4ja36">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-2775" data-mp="9p45bvm6ki2l" />
              </button>
            </header>
            <div className="px-6 py-6" data-id="element-2776" data-mp="3rv0ajyglolc6">
              <Content data-id="element-2777" data-mp="3ry4lrfshghcc" />
            </div>
          </motion.aside>
        </motion.div>}
    </AnimatePresence>;
}